import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors cursor-pointer disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
  {
    variants: {
      variant: {
        // The hygge.software buttons, taken from the site's stylesheet. [HW-926]
        //   default     its `.btn[data-cta]` — the main action on a surface.
        //               Mint fill with its own ink in both themes; white on
        //               mint is 2:1.
        //   secondary   its plain `.btn`, and its selected picklist item:
        //               surface, accent-text label, accent-text hairline at
        //               55%. Also what a "selected" or navigational control
        //               wears — a preset chip, a page number, a month stepper.
        //   destructive the default's shape in the site's coral, with the same
        //               ink (4.9:1), for delete and remove. The site has no
        //               delete button of its own to copy.
        default:
          "bg-cta text-cta-foreground hover:bg-cta/90 border border-transparent shadow-xs",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 border border-transparent shadow-xs focus-visible:ring-destructive",
        outline:
          "border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-card text-hygge border border-hygge/55 hover:border-hygge hover:bg-muted",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 border border-transparent",
        link: "text-hygge underline-offset-4 hover:underline border border-transparent",
        // Was a violet gradient in both themes. The brand has one primary
        // colour, so this now draws the default's mint, a notch more raised.
        accent:
          "bg-cta text-cta-foreground hover:bg-cta/90 border border-transparent shadow-md",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
